import {
  Grid,
  Paper,
  withStyles,
  Button,
  Typography,
  WithStyles,
  LinearProgress
} from '@material-ui/core'
import { Fragment, FC } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { openDialog } from '../../actions/dialogs'
import {
  changePassword,
  updateUserInformation,
  logout
} from '../../actions/users'
import Container from '../../components/Container'
import { Message } from '../../components/Messages'
import profileImage from '../../images/logInImage.svg'
import { objectNotEmpty } from '../../libs/objects'
import {
  selectIsAdminUser,
  selectUserAttributes,
  selectUserIsAuthenticated
} from '../../selectors/users'
import { UserAttributes } from '../../services/auth/types'
import { profileStyles } from '../../styles'
import { StateInterface } from '../../types/states'
import { bindSubmitActionToPromise } from '../../utils/forms'
import Dialog from '../Dialog'
import { ChangePasswordForm, EditProfileInfoForm } from './Forms'

const mapStateToProps = (state: StateInterface) => {
  const stateUserAttributes = selectUserAttributes(state)
  return {
    isAdmin: selectIsAdminUser(state),
    isAuthenticated: selectUserIsAuthenticated(state),
    userAttributes: objectNotEmpty<UserAttributes>(stateUserAttributes)
      ? stateUserAttributes
      : undefined
  }
}

type PropsFromState = ReturnType<typeof mapStateToProps>

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateUserInfo: bindSubmitActionToPromise(dispatch, updateUserInformation),
  changeUserPassword: bindSubmitActionToPromise(dispatch, changePassword),
  openDialogAction: (id: string) => dispatch(openDialog(id)),
  logoutUser: () => dispatch(logout())
})

type DispatchProps = ReturnType<typeof mapDispatchToProps>

type ProfileProps = PropsFromState &
  DispatchProps &
  WithTranslation &
  WithStyles

const Profile: FC<ProfileProps> = ({
  updateUserInfo,
  changeUserPassword,
  userAttributes,
  isAuthenticated,
  isAdmin,
  classes,
  openDialogAction,
  logoutUser,
  t
}: ProfileProps) => {
  if (!isAuthenticated) {
    return <p />
  }
  if (!userAttributes) {
    return <LinearProgress />
  } else {
    const {
      name,
      email,
      phone_number: phone,
      'custom:language': language
    } = userAttributes

    const profileInfo = {
      name,
      // eslint-disable-next-line camelcase
      phone_number: phone,
      'custom:language': language
    }
    const editProfileProps = {
      initialValues: profileInfo,
      updateUserInformation: updateUserInfo
    }

    return (
      <Fragment>
        <Grid
          container
          justify='space-around'
          style={{ backgroundColor: '#fafafa', minHeight: '100vh' }}
        >
          <Container alignCenter={true} className={classes.container}>
            <Grid container direction='row' justify='space-evenly'>
              <Grid item md={6} lg={6} xl={5}>
                <Grid item>
                  <Typography
                    gutterBottom
                    variant='h5'
                    className={classes.greetings}
                  >
                    {t('profile.greetings')} {name},
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.loggedInStatus}>
                    {t('profile.loggedInStatus')} {email}
                  </Typography>
                </Grid>
                <Grid item sm={undefined} className={classes.imageContainer}>
                  <img
                    className={classes.image}
                    src={profileImage}
                    alt='Man with suit'
                  />
                </Grid>
              </Grid>
              <Grid item className={classes.form} md={6} lg={6} xl={5}>
                <Paper className={classes.formPaper}>
                  {isAdmin && (
                    <Typography variant='subtitle1' gutterBottom>
                      {t('profile.adminUserAccess')}
                    </Typography>
                  )}
                  <Typography variant='h6' className={classes.editTitle}>
                    {t('editProfile.edit')}:
                  </Typography>
                  <EditProfileInfoForm {...editProfileProps} />
                  <Typography variant='h6' className={classes.changePassTitle}>
                    {t('changePw.change')}:
                  </Typography>
                  <ChangePasswordForm changePassword={changeUserPassword} />
                  <Button
                    variant='outlined'
                    color='secondary'
                    onClick={() => openDialogAction('ConfirmLogoutDialog')}
                    className={classes.logoutButton}
                  >
                    {t('navigation.logOut')}
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Grid>
        <Dialog
          dialogId='ConfirmLogoutDialog'
          noOpenDialogButton
          title={t('logout.label')}
          confirmDialogSubmitAction={logoutUser}
          Content={<Message message={t('logout.confirmMessage')} />}
        />
      </Fragment>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(profileStyles)(withTranslation()(Profile)))
